import CanvasObject from "./CanvasObject";
import TextObject from "./TextObject";
import {fabric} from "fabric";

import apiRequestHelper from "./../../api/requestHelper";

class TextboxObject extends TextObject {

    static TYPE_TITLE = 'textbox';

    /**
     * The alignment of the text element.
     * @type string
     */
    _textAlign;
    _textAlignIsManipulable;

    /**
     * If the value of the property is true the content can be defined in a text area.
     */
    _multilineEnabled;

    /**
     * Create a TextboxObject.
     * @param {Canvas} canvas Instance of the canvas on which the object is displayed.
     * @param {string} content The content of the text element.
     */
    constructor(canvas, content) {
        super(canvas, content);

        this._textAlign = 'left';
        this._textAlignIsManipulable = true;
        this._multilineEnabled = false;
    }

    /**
     * Returns a new FabricObject that can be added to the canvas.
     * @override
     * @returns {Promise<fabric.Object>}
     * @throws Will throw an error if the child not overrides the method.
     */
    async createFabricObject() {

        if (this.getVisibility() === 2 && window['w2p_admin'] !== true)
            return null;

        const textFabricObject = new fabric.Textbox(this.getText(), {
            id: this.getId(),
            width: this.getWidth() || null,
            selectable: this.getIsSelectable() || window['w2p_admin'] === true,
            left: this.getLeft(),
            top: this.getTop(),
            fontSize: this.getFontSize(),
            fontFamily: this.getFontFamily() || 'Times New Roman',
            fill: this.getFill(),
            angle: this.getAngle(),
            scaleX: this.getScaleX(),
            scaleY: this.getScaleY(),
            textAlign: this.getTextAlign(),
            borderColor: this.getExceedsBoundingBox() ? 'red' : 'rgba(102,153,255,0.75)',
            splitByGrapheme: true
        });

        this.setHeight( textFabricObject.height );
        this.setWidth( textFabricObject.width );

        return textFabricObject;

    }

    /**
     * A post request is sent to the API and attempts to create the object.
     * @param {number} configurationViewId
     * @return {Promise<void>}
     */
    async save(configurationViewId) {

        if (this._print === false && window['w2p_admin'] !== true) {
            return;
        }

        this.readFromFabricObject();

        const image = await apiRequestHelper.requestApiRoute(
            'post',
            '/image',
            {
                image: await this.createBase64Image()
            });

        await apiRequestHelper.requestApiRoute(
            'post',
            '/object',
            {
                serializedObject: {
                    isSelectable: this.getIsSelectable(),
                    deletable: this.getDeletable(),
                    label: {
                        value: this.getLabel()
                    },
                    labelSecondLanguage: {
                        value: this.getLabelSecondLanguage()
                    },
                    fill: {
                        value: this.getFill(),
                        manipulable: this.getFillIsManipulable(),
                        whitelist: this.getFillWhitelist()
                    },
                    left: {
                        value: this.getLeft(),
                        manipulable: this.getLeftIsManipulable(),
                        whitelist: this.getLeftWhitelist()
                    },
                    canvasRelativeLeft: {
                        value: this.getLeftBoundingBox(),
                        manipulable: this.getLeftBoundingBoxIsManipulable(),
                        whitelist: this.getLeftBoundingBoxWhitelist()
                    },
                    top: {
                        value: this.getTop(),
                        manipulable: this.getTopIsManipulable(),
                        whitelist: this.getTopWhitelist()
                    },
                    canvasRelativeTop: {
                        value: this.getTopBoundingBox(),
                        manipulable: this.getTopBoundingBoxIsManipulable(),
                        whitelist: this.getTopBoundingBoxWhitelist()
                    },
                    width: {
                        value: this.getScaledWidth(),
                        manipulable: this.getWidthIsManipulable(),
                        whitelist: this.getWidthWhitelist()
                    },
                    height: {
                        value: this.getHeight(),
                        manipulable: this.getHeightIsManipulable(),
                        whitelist: this.getHeightWhitelist()
                    },
                    angle: {
                        value: this.getAngle(),
                        manipulable: this.getAngleIsManipulable(),
                        whitelist: this.getAngleWhitelist()
                    },
                    type: this.constructor.TYPE_TITLE,
                    fontSize: {
                        value: this.getFontSize(),
                        manipulable: this.getFontSizeIsManipulable(),
                        whitelist: this.getFontSizeWhitelist()
                    },
                    fontFamily: {
                        value: this.getFontFamily() || 'Times New Roman',
                        manipulable: this.getFontFamilyIsManipulable(),
                        whitelist: this.getFontFamilyWhitelist()
                    },
                    text: {
                        value: this.getText(),
                        manipulable: this.getTextIsManipulable(),
                        whitelist: this.getTextWhitelist()
                    },
                    textSecondLanguage: {
                        value: this.getTextSecondLanguage()
                    },
                    textAlign: {
                        value: this.getTextAlign(),
                        manipulable: this.getTextAlignIsManipulable()
                    },
                    print: this._print,
                    image: null,
                    scaleX: this.getScaleX(),
                    scaleY: this.getScaleY(),
                    isMultilineEnabled: this.getIsMultilineEnabled(),
                    visibility: this.getVisibility(),
                    zPosition: this.getZPosition(),
                    exceedsBoundingBox: this.getExceedsBoundingBox()
                },
                productConfigurationViewId: configurationViewId,
                imageId: image.id
            },
            error => AppDebug.api.events.createCanvasObjectFailed({error})
        );

    }

    /**
     * Adapt this instance to the new values of the matching FabricObject properties on the canvas.
     * @override
     * @return fabric.Object
     */
    readFromFabricObject() {

        const fabricObject = super.readFromFabricObject();

        if (! fabricObject)
            return null;

        // this.setFill( fabricObject.fill );
        // this.setFontSize( fabricObject.fontSize );
        // this.setFontFamily( fabricObject.fontFamily );
        // this.setText( fabricObject.text );

        return fabricObject;

    }

    /**
     * Creates a image from the text element which is used to create the print file on the server.
     * @returns {Promise<string>}
     */
    async createBase64Image() {

        const object = this.getMatchingFabricObject();

        const sizes = object.getBoundingRect();

        // TODO no use of _factor, create getter
        // sizes.width = sizes.width * this.getCanvas()._factor;
        // sizes.height = sizes.height * this.getCanvas()._factor;

        const textClone = new fabric.Textbox(object.text, {
            left: 0,
            top: object.top - sizes.top,
            fill: object.fill,
            scaleX: 60,//object.scaleX,
            scaleY: 60,//object.scaleY,
            // angle: object.angle,
            fontSize: object.fontSize,
            fontFamily: object.fontFamily,
            width: object.width,
            textAlign: object.textAlign
        });

        const targetHeight = (this.getCanvas()._dpi * this.getScaledHeightInMm()) / 25.4;
        const targetWidth = (this.getCanvas()._dpi * this.getScaledWidthInMm()) / 25.4;

        console.log({
            scaleY: parseFloat(
                Number(targetHeight / this.getHeight()).toFixed(4)
            ),
            scaleX: parseFloat(
                Number(targetWidth / this.getWidth()).toFixed(4)
            ),
            currentHeight: this.getHeight(),
            targetHeight: targetHeight,
            currentWidth: this.getWidth(),
            targetWidth: targetWidth
        });

        textClone.set({
            scaleY: parseFloat(
                Number(targetHeight / this.getHeight()).toFixed(4)
            ),
            scaleX: parseFloat(
                Number(targetWidth / this.getWidth()).toFixed(4)
            )
        });

        const cloneSizes = textClone.getBoundingRect();

        textClone.set({
            top: textClone.top - cloneSizes.top,
            left: textClone.left - cloneSizes.left,
        });

        const pngCanvas = new fabric.Canvas(
            this.getCanvas().getCanvas().lowerCanvasEl.id.replace('canvas-', 'canvas-to-png-'),
            {
                width: targetWidth + (object.strokeWidth * 2),
                height: targetHeight + (object.strokeWidth * 2)
            }
        );

        pngCanvas.add(textClone);

        pngCanvas.renderAll();

        return pngCanvas.toDataURL();

        window.open(pngCanvas.toDataURL(),'_blank');

    }

    /**
     * Each type of object can define its own view in which controls are defined for the object.
     * @returns {string} Filename
     */
    getViewInObjectsConfigurator() {

        return 'TextboxCanvasObject';

    }

    getTextAlign() {
        return this._textAlign;
    }

    setTextAlign(value) {
        this._textAlign = value;
    }

    getTextAlignIsManipulable() {
        return this._textAlignIsManipulable;
    }

    setTextAlignIsManipulable(value) {
        this._textAlignIsManipulable = value;
    }

    getIsMultilineEnabled() {
        return this._multilineEnabled;
    }

    setIsMultilineEnabled(value) {
        this._multilineEnabled = value;
    }

    isVisibleInSidebar() {

        if (
            super.isVisibleInSidebar() ||
            this.getTextAlignIsManipulable()
        ) {
            return true;
        }

        return false;

    }

}

export default TextboxObject;